import { defineMessages } from "react-intl";

import { translate } from "../../helpers/translate";

import HomePageRoute from "../Routes/HomePageRoute";
import PageRoute from "../Routes/PageRoute";
import ParticipativeModuleRoute from "./ParticipativeModuleRoute";
import ContributionFormRoute from "../Routes/ContributionFormRoute";
import ContributionEditRoute from "./ContributionEditRoute";
import ContributionRoute from "../Routes/ContributionRoute";
import ContributionsDatavizGraphRoute from "../Routes/ContributionsDatavizGraphRoute";
import ContributionsDatavizGraphThemeRoute from "../Routes/ContributionsDatavizGraphThemeRoute";
import ContributionsDatavizListRoute from "../Routes/ContributionsDatavizListRoute";
import ContributionsDatavizListThemeRoute from "../Routes/ContributionsDatavizListThemeRoute";
import ContributionsDatavizSearchRoute from "../Routes/ContributionsDatavizSearchRoute";
import ContributorContactRoute from "./ContributorContactRoute";
import AccountConfirmationRoute from "../Routes/AccountConfirmationRoute";
import AccountUnlockingRoute from "../Routes/AccountUnlockingRoute";
import PasswordEditFormRoute from "../Routes/PasswordEditFormRoute";

// User profile routes
import ProfileLayoutRoute from "../Routes/ProfileLayoutRoute";
import ConfigureAccountRoute from "../Routes/ConfigureAccountRoute";
import PostSignUpFormRoute from "../Routes/PostSignUpFormRoute";
import ProfileEditionRoute from "../Routes/ProfileEditionRoute";
import ProfileSettingsRoute from "../Routes/ProfileSettingsRoute";
import ProfileContributionsRoute from "../Routes/ProfileContributionsRoute";
import ProfileContributionRoute from "../Routes/ProfileContributionRoute";
import CityWalkWaypointRoute from "./CityWalkWaypointRoute";
import CityWalkWaypointContributionFormRoute from "./CityWalkWaypointContributionFormRoute";
import UsersMapRoute from "../Routes/UsersMapRoute";
import InformativeMapRoute from "../Routes/InformativeMapRoute";
import EventModalRoute from "./EventModalRoute";
import CityWalkHomeRoute from "./CityWalkHomeRoute";

export const intlMessages = defineMessages({
  configureAccount: {
    id: "routes.configureAccount",
    defaultMessage: "configuration-du-compte"
  },
  postSignUpForm: {
    id: "routes.postSignUpForm",
    defaultMessage: "creation-de-compte"
  },
  pages: {
    id: "routes.pages",
    defaultMessage: "pages"
  },
  participativeModules: {
    id: "routes.participativeModules",
    defaultMessage: "participation"
  },
  map: {
    id: "routes.map",
    defaultMessage: "carte"
  },
  maps: {
    id: "routes.maps",
    defaultMessage: "cartes"
  },
  usersMaps: {
    id: "routes.usersMaps",
    defaultMessage: "cartes-des-utilisateurs"
  },
  informativeMaps: {
    id: "routes.informativeMaps",
    defaultMessage: "cartes-informatives"
  },
  library: {
    id: "routes.library",
    defaultMessage: "bibliotheque"
  },
  profile: {
    id: "routes.profile",
    defaultMessage: "profil"
  },
  edit: {
    id: "routes.edit",
    defaultMessage: "edition"
  },
  parameters: {
    id: "routes.parameters",
    defaultMessage: "parametres"
  },
  contributions: {
    id: "routes.contributions",
    defaultMessage: "contributions"
  },
  subscription: {
    id: "routes.subscription",
    defaultMessage: "inscription"
  },
  contribute: {
    id: "routes.contribute",
    defaultMessage: "contribuer"
  },
  submit: {
    id: "routes.submit",
    defaultMessage: "soumettre"
  },
  events: {
    id: "routes.events",
    defaultMessage: "evenements"
  },
  confirmation: {
    id: "routes.confirmation",
    defaultMessage: "confirmation"
  },
  unlock: {
    id: "routes.unlock",
    defaultMessage: "unlock"
  },
  users: {
    id: "routes.users",
    defaultMessage: "users"
  },
  password: {
    id: "routes.password",
    defaultMessage: "password"
  },
  search: {
    id: "routes.search",
    defaultMessage: "recherche"
  },
  themes: {
    id: "routes.themes",
    defaultMessage: "thematiques"
  },
  graph: {
    id: "routes.graph",
    defaultMessage: "graphique"
  },
  list: {
    id: "routes.list",
    defaultMessage: "liste"
  },
  anonymization: {
    id: "routes.anonymization",
    defaultMessage: "anonymisation"
  }
});

const routeTranslations = Object.keys(intlMessages).reduce((routes, key) => {
  routes[key] = translate(intlMessages[key]);
  return routes;
}, {});

// Route names
const {
  configureAccount,
  postSignUpForm,
  pages,
  participativeModules,
  usersMaps,
  informativeMaps,
  profile,
  edit,
  parameters,
  contributions,
  contribute,
  events,
  users,
  confirmation,
  password,
  unlock,
  search,
  themes,
  graph,
  list,
  anonymization
} = routeTranslations;

// Shared properties of the contribution modal route
const contributionRoute = {
  path: `${contributions}/:contributionSlug`,
  isModal: true,
  Component: ContributionRoute,
  isContributionRoute: true,
  rootKey: "contribution"
};

// Shared properties of the new contribution form modal route
const contributeRoute = {
  path: `${contribute}`,
  isModal: true,
  Component: ContributionFormRoute,
  rootKey: "newContribution"
};

export const routesConfig = [
  {
    name: "configureAccount",
    path: `${configureAccount}`,
    Component: ConfigureAccountRoute
  },
  {
    name: "postSignUpForm",
    path: `${postSignUpForm}`,
    Component: PostSignUpFormRoute,
    isModal: true
  },
  {
    name: "page",
    path: `${pages}/:id`,
    Component: PageRoute
  },
  {
    name: "participativeModule",
    path: `${participativeModules}/:participativeModuleSlug`,
    Component: ParticipativeModuleRoute,
    children: [
      {
        name: "newContribution",
        ...contributeRoute
      },
      {
        name: "contribution",
        ...contributionRoute
      },
      {
        name: "editContribution",
        path: `${contributions}/:contributionSlug/${edit}`,
        isModal: true,
        Component: ContributionEditRoute
      },
      {
        name: "contributorContact",
        path: `${contributions}/:contributionSlug/contact`,
        isModal: true,
        Component: ContributorContactRoute
      },
      {
        name: "participativeModuleEvent",
        path: `${events}/:eventSlug`,
        isModal: true,
        Component: EventModalRoute
      },
      {
        path: `${graph}`,
        children: [
          {
            name: "datavizGraph",
            index: true,
            path: "",
            Component: ContributionsDatavizGraphRoute,
            routesGroup: "dataviz"
          },
          {
            name: "datavizGraphTheme",
            path: `${themes}/:themeSlug`,
            Component: ContributionsDatavizGraphThemeRoute,
            routesGroup: "dataviz",
            children: [
              {
                name: "datavizGraphThemeContribution",
                routesGroup: "dataviz",
                ...contributionRoute
              },
              {
                name: "datavizGraphThemeNewContribution",
                routesGroup: "dataviz",
                ...contributeRoute
              }
            ]
          },
          {
            Component: ContributionsDatavizGraphRoute,
            children: [
              {
                name: "datavizGraphNewContribution",
                routesGroup: "dataviz",
                ...contributeRoute
              }
            ]
          }
        ]
      },
      {
        path: `${list}`,
        children: [
          {
            name: "datavizList",
            index: true,
            path: "",
            Component: ContributionsDatavizListRoute,
            routesGroup: "dataviz"
          },
          {
            name: "datavizListTheme",
            path: `${themes}/:themeSlug`,
            Component: ContributionsDatavizListThemeRoute,
            routesGroup: "dataviz",
            children: [
              {
                name: "datavizListThemeContribution",
                routesGroup: "dataviz",
                ...contributionRoute
              },
              {
                name: "datavizListThemeNewContribution",
                routesGroup: "dataviz",
                ...contributeRoute
              }
            ]
          },
          {
            name: "datavizListNewContribution",
            routesGroup: "dataviz",
            ...contributeRoute
          }
        ]
      },
      {
        name: "datavizSearch",
        path: `${search}`,
        Component: ContributionsDatavizSearchRoute,
        routesGroup: "dataviz",
        children: [
          {
            name: "datavizSearchContribution",
            ...contributionRoute
          },
          {
            name: "datavizSearchNewContribution",
            ...contributeRoute
          }
        ]
      },
      {
        name: "cityWalkHome",
        path: "",
        Component: CityWalkHomeRoute
      },
      {
        name: "cityWalkWaypoint",
        path: ":cityWalkWaypointId",
        Component: CityWalkWaypointRoute,
        paramConstraints: {
          // Ensure that the cityWalkWaypointId is not the "contributions" string, to
          // avoid the route to be matched when looking for contribution modal route
          // parent.
          cityWalkWaypointId: new RegExp(
            `^(?!(${contributions}|${graph}|${list}|${search})$).+`
          )
        },
        children: [
          {
            name: "cityWalkWaypointContributionForm",
            path: `${contribute}`,
            Component: CityWalkWaypointContributionFormRoute,
            isModal: true
          }
        ]
      }
    ]
  },
  {
    name: "usersMap",
    path: `${usersMaps}/:usersMapSlug`,
    Component: UsersMapRoute,
    children: [
      {
        name: "usersMapEvent",
        path: `${events}/:eventSlug`,
        isModal: true,
        Component: EventModalRoute
      }
    ]
  },
  {
    name: "informativeMap",
    path: `${informativeMaps}/:informativeMapSlug`,
    Component: InformativeMapRoute,
    children: [
      {
        name: "informativeMapEvent",
        path: `${events}/:eventSlug`,
        isModal: true,
        Component: EventModalRoute
      }
    ]
  },
  {
    name: "profile",
    path: `${profile}`,
    Component: ProfileLayoutRoute,
    children: [
      {
        name: "profileEdition",
        path: `${edit}`,
        Component: ProfileEditionRoute
      },
      {
        name: "profileSettings",
        path: `${parameters}`,
        Component: ProfileSettingsRoute
      },
      {
        name: "profileContributions",
        path: `${contributions}`,
        exact: true,
        Component: ProfileContributionsRoute,
        children: [
          {
            name: "profileContribution",
            path: ":contributionSlug",
            exact: true,
            isModal: true,
            Component: ProfileContributionRoute
          }
        ]
      }
    ]
  },
  {
    path: `${users}`,
    children: [
      {
        name: "confirmAccount",
        path: `${confirmation}`,
        isModal: true,
        Component: AccountConfirmationRoute
      },
      {
        name: "editPassword",
        path: `${password}/${edit}`,
        isModal: true,
        Component: PasswordEditFormRoute
      },
      {
        name: "unlockAccount",
        path: `${unlock}`,
        isModal: true,
        Component: AccountUnlockingRoute
      },
      // This route is never rendered, as its sole purpose is to provide the translated
      // path in the `pathFor` and `navigateTo` helper functions, but the actual route is
      // a Rails route that is managed by an actual controller action.
      {
        name: "anonymization",
        path: `${anonymization}`
      }
    ]
  },
  {
    name: "home",
    path: "",
    Component: HomePageRoute,
    index: true
  }
];

// Builds a map of all nested routes by their name to allow easy external access to all
// the routes
export const routesMap = routesConfig.reduce((acc, routeConfig) => {
  const addRouteAndChildren = (route, { prefix = "", parent = null } = {}) => {
    const routeConfig = { ...route, parent };
    const isLayoutRoute = routeConfig.path === void 0;
    routeConfig.path = isLayoutRoute ? prefix : [prefix, route.path].join("/");

    if (!isLayoutRoute) {
      acc[route.name] = routeConfig;
    }

    route.children?.forEach(childRoute =>
      addRouteAndChildren(childRoute, { prefix: routeConfig.path, parent: routeConfig })
    );
  };

  addRouteAndChildren(routeConfig);

  return acc;
}, {});
